import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { X } from 'lucide-react';

export interface CalendarFilterValues {
  client: string;
  area: string;
  team: string;
  category: string;
}

interface CalendarFiltersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: CalendarFilterValues;
  onFiltersChange: (filters: CalendarFilterValues) => void;
  onApply: () => void;
  onClear: () => void;
}

export function CalendarFiltersModal({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
  onApply,
  onClear,
}: CalendarFiltersModalProps) {
  const updateFilter = (key: keyof CalendarFilterValues, value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const activeFiltersCount = Object.values(filters).filter(
    (value) => value !== '' && value !== 'all',
  ).length;

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 animate-in fade-in duration-200"
        onClick={() => onOpenChange(false)}
      />

      {/* Side Panel */}
      <div className="fixed right-0 top-0 h-full w-[400px] bg-card border-l border-border z-50 flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="font-semibold text-foreground">Filters</h2>
            {activeFiltersCount > 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                {activeFiltersCount} active filter{activeFiltersCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Filters Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Client Filter */}
            <div className="space-y-2">
              <Label className="text-foreground">Client</Label>
              <Select
                value={filters.client}
                onValueChange={(value) => updateFilter('client', value)}
              >
                <SelectTrigger className="bg-background border-border text-foreground">
                  <SelectValue placeholder="All clients" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">All clients</SelectItem>
                  <SelectItem value="wastewater-authority">Wastewater Authority</SelectItem>
                  <SelectItem value="city-water">City Water Department</SelectItem>
                  <SelectItem value="municipal-services">Municipal Services</SelectItem>
                  <SelectItem value="environmental-agency">Environmental Agency</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Area Filter */}
            <div className="space-y-2">
              <Label className="text-foreground">Area</Label>
              <Select value={filters.area} onValueChange={(value) => updateFilter('area', value)}>
                <SelectTrigger className="bg-background border-border text-foreground">
                  <SelectValue placeholder="All areas" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">All areas</SelectItem>
                  <SelectItem value="zone-a">Zone A - Treatment</SelectItem>
                  <SelectItem value="zone-b">Zone B - Distribution</SelectItem>
                  <SelectItem value="zone-c">Zone C - Collection</SelectItem>
                  <SelectItem value="zone-d">Zone D - Pumping</SelectItem>
                  <SelectItem value="central">Central Facility</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Team Filter */}
            <div className="space-y-2">
              <Label className="text-foreground">Team</Label>
              <Select value={filters.team} onValueChange={(value) => updateFilter('team', value)}>
                <SelectTrigger className="bg-background border-border text-foreground">
                  <SelectValue placeholder="All teams" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">All teams</SelectItem>
                  <SelectItem value="1">Operations</SelectItem>
                  <SelectItem value="2">Maintenance</SelectItem>
                  <SelectItem value="3">Quality Control</SelectItem>
                  <SelectItem value="4">IT & Automation</SelectItem>
                  <SelectItem value="5">Compliance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="space-y-2">
              <Label className="text-foreground">Category</Label>
              <Select
                value={filters.category}
                onValueChange={(value) => updateFilter('category', value)}
              >
                <SelectTrigger className="bg-background border-border text-foreground">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">All categories</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="inspection">Inspection</SelectItem>
                  <SelectItem value="calibration">Calibration</SelectItem>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="testing">Testing</SelectItem>
                  <SelectItem value="installation">Installation</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border bg-card/50">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClear}
              className="flex-1 border-border hover:bg-accent"
            >
              Clear All
            </Button>
            <Button
              onClick={() => {
                onApply();
                onOpenChange(false);
              }}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
